import React, { useState, useRef, useEffect, useContext } from "react";
import {
  ChakraProvider,
  Box,
  Heading,
  Button,
  Grid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Input,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Center,
} from "@chakra-ui/react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import GridLayout, { Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import DragItem from "./component/Forms/DragItem";
import DropZone from "./component/Forms/DropZone";
import FormElement from "./component/Forms/FormElement";
import { GiVerticalFlip } from "react-icons/gi";
import { TiSortNumerically } from "react-icons/ti";
import { AiOutlineMinus } from "react-icons/ai";

import {
  FaHeading,
  FaParagraph,
  FaRegCaretSquareDown,
  FaTextWidth,
  FaRegCalendarAlt,
} from "react-icons/fa";
import {
  MdMailOutline,
  MdRadioButtonChecked,
  MdCheckBox,
  MdFileUpload,
  MdImageAspectRatio,
} from "react-icons/md";
import { IoIosCloseCircle } from "react-icons/io";

import FormModal from "./component/Forms/dummyFormData";
import ElementContainer from "./component/Forms/ElementContainer";
import { getInitialValues } from "./component/Forms/constant";
import CustomInput from "components/common/customInput/CustomInput";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { Paginationlimit } from "components/common/functions/variable";
import ModelButton from "./component/Forms/dummyFormData";
import ViewFormModal from "App/Onboarding/forms/component/ViewFormModal";

const DesignElements = [
  { label: "Global", value: "global" },
  { label: "Form", value: "form" },
  { label: "Form Group", value: "formGroup" },
  { label: "Form Control", value: "formControl" },
  { label: "Label", value: "label" },
  { label: "Heading", value: "heading" },
  { label: "Help Text", value: "helpText" },
];

const borderStyles = [
  { label: "None", value: "none" },
  { label: "Solid", value: "solid" },
  { label: "Dashed", value: "dashed" },
  { label: "Dotted", value: "dotted" },
  { label: "Double", value: "double" },
];

const textTransform = [
  { label: "Upper Case", value: "uppercase" },
  { label: "Lower Case", value: "lowercase" },
  { label: "Capitalize", value: "capitalize" },
];

const textDecoration = [
  { label: "Over Line", value: "overline" },
  { label: "Under Line", value: "underline" },
  { label: "Line Through", value: "line-through" },
];

type OnboardingProps = {
  type: any;
  formValues?: any;
  handleGet?: any;
  id?: any;
};

const CustomOnboarding: React.FC<OnboardingProps> = ({
  type,
  formValues,
  handleGet,
  id,
}) => {
  const [modalOpen, setModalOpen] = useState<any>({
    open: false,
    data: null,
    type: undefined,
  });
  const [elements, setElements] = useState<any[]>([]);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [designElements, setDesignElements] = useState<any[]>([]);
  const [formName, setFormName] = useState<any>("New Form");
  const { toastNotify, accountDetails } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activePage, setActivePage] = useState<any>({
    page: 1,
    data: null,
  });

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // While editing setting initial elements and designElements
  useEffect(() => {
    if (type === "edit" && formValues) {
      setElements(formValues?.values[0]?.elements);
      setDesignElements(formValues?.values[0]?.designElements);
    }
  }, [formValues]);

  // for design element
  const handleChange = (element: any, name: any, value: any) => {
    setDesignElements((prevState) => ({
      ...prevState,
      [element]: {
        ...prevState[element],
        [name]: value,
      },
    }));
  };

  // to delete color setting field to reset values
  const handleFieldDelete = (element: any, field: any) => {
    setDesignElements((prevState) => {
      const updatedElement = { ...prevState[element] };
      delete updatedElement[field];

      return {
        ...prevState,
        [element]: updatedElement,
      };
    });

    // To change the color of input field and setting it to default black color
    const input = document.querySelector(
      `input[name=${field}]`
    ) as HTMLInputElement;
    if (input) {
      input.value = "#000000";
    }
  };

  // to delete the entire element
  const handleDeleteElement = (elementKey: any) => {
    // Update the state to delete the specified element
    setDesignElements((prevState) => {
      const updatedElements = { ...prevState };
      delete updatedElements[elementKey];
      return updatedElements;
    });
  };

  const handleDrop = (item: {
    type: any;
    offset: { x: number; y: number };
  }) => {
    const dropZoneRect = dropZoneRef.current?.getBoundingClientRect();
    if (!dropZoneRect) return;

    const gridOffset = {
      x: Math.floor((item.offset.x - dropZoneRect.left) / 100),
      y: Math.floor((item.offset.y - dropZoneRect.top) / 50),
    };

    const newElement = {
      id: elements.length,
      type: item.type,
      x: gridOffset.x,
      y: gridOffset.y,
      w: 4,
      h: 2,
      attributes: getInitialValues(item.type, elements.length),
    };
    setElements([...elements, newElement]);
  };

  const handleLayoutChange = (layout: Layout[]) => {
    setElements(
      layout.map((item: Layout) => {
        const element = elements.find((el) => el.id === parseInt(item.i, 10));
        return {
          ...element,
          x: item.x,
          y: item.y,
          w: item.w,
          h: element.h,
        };
      })
    );
  };

  const handleDelete = (id: number) => {
    setElements(elements.filter((el) => el.id !== id));
  };

  const handleExport = () => {
    // const dataStr =
    //   "data:text/json;charset=utf-8," +
    //   encodeURIComponent(JSON.stringify(elements));
    // const downloadAnchorNode = document.createElement("a");
    // downloadAnchorNode.setAttribute("href", dataStr);
    // downloadAnchorNode.setAttribute("download", "form_layout.json");
    // downloadAnchorNode.click();
    // downloadAnchorNode.remove();
    console.log({
      formName: formName,
      elements: elements,
      designElements: designElements,
    });
  };


  const submitForm = async () => {
    try {
      let values = {
        formName: formName,
        elements: elements,
        designElements: designElements,
        activeWorkflow:accountDetails?.defaultWorkflow
      };
      setLoading(true);
      if (values) {
        const response: any = await appRequest("form", "create", values);
        if (response.status === "success") {
          toastNotify({
            title: `Form Created successfully`,
            message: response.message || "Something went wrong.",
            type: "success",
          });
        } else {
          toastNotify({
            title: `Error getting the Form.`,
            message: response.message || "Something went wrong.",
            type: "error",
          });
        }
      }
      resetForm();
    } catch (err: any) {
      toastNotify({
        title: `Error getting the Form.`,
        message: err.message || "Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateForm = async (valuess: any) => {
    try {
      console.log("valeeee", valuess)
      let obj: any = {
        values: valuess,
        id: id,
      };
      setLoading(true);
      if (valuess) {
        const response: any = await appRequest("form", "update", obj);
        if (response.status === "success") {
          toastNotify({
            title: `Form updated successfully`,
            message: response.message || "Something went wrong.",
            type: "success",
          });
          handleGet();
        } else {
          toastNotify({
            title: `Error getting the Form.`,
            message: response.message || "Something went wrong.",
            type: "error",
          });
        }
      }
    } catch (err: any) {
      toastNotify({
        title: `Error getting the Form.`,
        message: err.message || "Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDesignElements([]);
    setElements([]);
    setFormName("New Form");
  };

  const handleClick = (elements: any, designElements: any) => {
    let valuess = {
      formName: formName,
      elements: elements,
      designElements: designElements,
    };
    if (type === "create") {
      submitForm();
    } else if (type === "edit") {
      updateForm(valuess);
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (e.target && typeof e.target.result === "string") {
          setElements(JSON.parse(e.target.result));
        }
      };
      reader.readAsText(file);
    }
  };
  useEffect(() => {
    console.log("designElement", designElements);
  }, [designElements]);

  // const renderDesignElements = (type: any) => {
  //   switch (type) {

  // }

  return (
    // <ChakraProvider>
    <React.Fragment>
      {activePage.page === 1 && (
        <DndProvider backend={HTML5Backend}>
          <Box p={4}>
            <Grid templateColumns="1fr 1.5fr 1fr" gap={4}>
              <Box flex="1" p={0} border="1px solid #e2e8f0" borderRadius={6}>
                <Tabs isFitted variant="enclosed">
                  <TabList>
                    <Tab>Field</Tab>
                    <Tab>Settings</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      <Grid templateColumns="1fr 1fr" gap={4}>
                        <DragItem type="logo">
                          <MdImageAspectRatio /> Logo
                        </DragItem>
                        <DragItem type="heading">
                          <FaHeading /> Heading
                        </DragItem>
                        <DragItem type="paragraph">
                          <FaParagraph /> Paragraph
                        </DragItem>
                        <DragItem type="text">
                          <FaTextWidth /> Input
                        </DragItem>
                        <DragItem type="number">
                          <TiSortNumerically /> Number
                        </DragItem>
                        <DragItem type="date">
                          <FaRegCalendarAlt /> Date Field
                        </DragItem>
                        <DragItem type="email">
                          <MdMailOutline /> Email
                        </DragItem>
                        <DragItem type="select">
                          <FaRegCaretSquareDown /> Select
                        </DragItem>
                        <DragItem type="textarea">
                          <FaRegCaretSquareDown /> Textarea
                        </DragItem>
                        <DragItem type="button">
                          <FaRegCaretSquareDown /> Button
                        </DragItem>
                        <DragItem type="file">
                          <MdFileUpload /> File
                        </DragItem>
                        <DragItem type="checkbox">
                          <MdCheckBox /> Checkbox
                        </DragItem>
                        <DragItem type="radio">
                          <MdRadioButtonChecked /> Radio
                        </DragItem>
                        <DragItem type="divider">
                          <AiOutlineMinus /> Divider
                        </DragItem>
                        <DragItem type="spacer">
                          <GiVerticalFlip /> Spacer
                        </DragItem>
                      </Grid>
                    </TabPanel>
                    <TabPanel>
                      <Grid gap={2}>
                        <>
                          <CustomInput
                            name="formName"
                            label="Enter Form Name"
                            value={formName}
                            onChange={(e: any) => setFormName(e.target.value)}
                          />
                          <Text fontSize="12px" color="blue.500">
                            Used to identify form on administration pages.
                          </Text>
                        </>
                      </Grid>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </Box>
              <Box
                flex="3"
                p={4}
                border="1px solid #e2e8f0"
                borderRadius={6}
                maxWidth="1000px"
                overflow={"auto"}
                h={"35rem"}
                className="customScrollBar"
              >
                <Heading size="md" mb={4}>
                  Form Preview
                </Heading>
                <Flex
                  gap={2}
                  alignItems="center"
                  mb={4}
                  justifyContent="space-between"
                >
                  <Button onClick={handleExport} w="7rem">
                    Export
                  </Button>
                  <Button
                    onClick={() => handleClick(elements, designElements)}
                    w="7rem"
                  >
                    Save Form
                  </Button>
                  <Button onClick={resetForm} w="7rem">
                    Reset Form
                  </Button>
                  <Button
                    onClick={() => {
                      setActivePage({
                        page: 2,
                        data: { elements, designElements },
                      });
                    }}
                    w="7rem"
                  >
                    Show Layout
                  </Button>
                </Flex>
                {/* <input type="file" accept=".json" onChange={handleImport} /> */}
                <DropZone onDrop={handleDrop} ref={dropZoneRef}>
                  <GridLayout
                    className="layout"
                    layout={elements.map((el) => ({
                      i: el.id.toString(),
                      x: el.x,
                      y: el.y,
                      w: el.w,
                      h: el.h,
                      static: false,
                    }))}
                    cols={8}
                    rowHeight={20}
                    width={520}
                    margin={[10, 10]}
                    onLayoutChange={handleLayoutChange}
                    isResizable
                    isDraggable
                  >
                    {elements.map((element) => (
                      <Box
                        key={element.id}
                        data-grid={{
                          x: element.x,
                          y: element.y,
                          w: element.w,
                          h: element.h,
                        }}
                        width="100%"
                      >
                        <FormElement
                          id={element.id}
                          type={element.type}
                          onDelete={handleDelete}
                          setElements={setElements}
                          setOpenModel={(dt: any) =>
                            setModalOpen({
                              open: true,
                              data: dt,
                              type: element.type,
                            })
                          }
                        />
                      </Box>
                    ))}
                  </GridLayout>
                </DropZone>
              </Box>
              <Box flex="3" border="1px solid #e2e8f0" borderRadius={6}>
                <Flex justifyContent="center" p={2}>
                  Design
                </Flex>
                <Divider />
                <Box maxH="440px" overflow={"auto"} className="customScrollBar">
                  {DesignElements.map((element: any, index: any) => (
                    <Accordion allowMultiple key={index}>
                      <AccordionItem>
                        <h2>
                          <AccordionButton
                            _expanded={{ bg: "teal.500", color: "white" }}
                          >
                            <Box as="span" flex="1" textAlign="left">
                              {element.label}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <Grid gap={2}>
                            <Text
                              fontSize="xs"
                              fontWeight="700"
                              color="gray.400"
                            >
                              Background
                            </Text>
                            <Divider orientation="horizontal" />
                            <FormControl>
                              <FormLabel>Background Color</FormLabel>
                              <Flex
                                justifyContent="space-between"
                                alignItems="center"
                                gap={2}
                              >
                                <Input
                                  type="color"
                                  name="bgColor"
                                  value={
                                    designElements[element.value]?.bgColor || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "bgColor",
                                      e.target.value
                                    )
                                  }
                                />
                                <IoIosCloseCircle
                                  fontSize="30px"
                                  color="#C53030"
                                  onClick={() =>
                                    handleFieldDelete(element.value, "bgColor")
                                  }
                                  cursor="pointer"
                                />
                              </Flex>
                            </FormControl>
                            <Text
                              fontSize="xs"
                              fontWeight="700"
                              color="gray.400"
                            >
                              Border
                            </Text>
                            <Divider orientation="horizontal" />
                            <Flex justifyContent="space-between" gap={2}>
                              <FormControl>
                                <FormLabel
                                  fontSize="sm"
                                  textTransform="capitalize"
                                >
                                  Border Style
                                </FormLabel>
                                <Select
                                  name="borderStyle"
                                  value={
                                    designElements[element.value]
                                      ?.borderStyle || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "borderStyle",
                                      e.target.value
                                    )
                                  }
                                >
                                  {borderStyles?.map((option) => (
                                    <option value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl>
                                <FormLabel>Border Width</FormLabel>
                                <Input
                                  name="borderWidth"
                                  value={
                                    designElements[element.value]
                                      ?.borderWidth || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "borderWidth",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                            </Flex>
                            <FormControl>
                              <FormLabel>Border Color</FormLabel>
                              <Flex
                                justifyContent="space-between"
                                alignItems="center"
                                gap={2}
                              >
                                <Input
                                  type="color"
                                  name="borderColor"
                                  value={
                                    designElements[element.value]
                                      ?.borderColor || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "borderColor",
                                      e.target.value
                                    )
                                  }
                                />
                                <IoIosCloseCircle
                                  fontSize="30px"
                                  color="#C53030"
                                  onClick={() =>
                                    handleFieldDelete(
                                      element.value,
                                      "borderColor"
                                    )
                                  }
                                  cursor="pointer"
                                />
                              </Flex>
                            </FormControl>
                            <Text
                              fontSize="xs"
                              fontWeight="700"
                              color="gray.400"
                            >
                              Spacing
                            </Text>
                            <Divider orientation="horizontal" />
                            <Flex justifyContent="space-between" gap={2}>
                              <FormControl>
                                <FormLabel>Margin</FormLabel>
                                <Input
                                  name="margin"
                                  value={
                                    designElements[element.value]?.margin || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "margin",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel>Padding</FormLabel>
                                <Input
                                  name="padding"
                                  value={
                                    designElements[element.value]?.padding || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "padding",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                            </Flex>
                            <Text
                              fontSize="xs"
                              fontWeight="700"
                              color="gray.400"
                            >
                              Text
                            </Text>
                            <Divider orientation="horizontal" />
                            <FormControl>
                              <FormLabel>Font Color</FormLabel>
                              <Flex
                                justifyContent="space-between"
                                alignItems="center"
                                gap={2}
                              >
                                <Input
                                  type="color"
                                  name="fontColor"
                                  value={
                                    designElements[element.value]?.fontColor ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "fontColor",
                                      e.target.value
                                    )
                                  }
                                />
                                <IoIosCloseCircle
                                  fontSize="30px"
                                  color="#C53030"
                                  onClick={() =>
                                    handleFieldDelete(
                                      element.value,
                                      "fontColor"
                                    )
                                  }
                                  cursor="pointer"
                                />
                              </Flex>
                            </FormControl>
                            <Flex justifyContent="space-between" gap={2}>
                              <FormControl>
                                <FormLabel>Font Size</FormLabel>
                                <Input
                                  name="fontSize"
                                  value={
                                    designElements[element.value]?.fontSize ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "fontSize",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel>Font Weight</FormLabel>
                                <Input
                                  name="fontWeight"
                                  value={
                                    designElements[element.value]?.fontWeight ||
                                    ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "fontWeight",
                                      e.target.value
                                    )
                                  }
                                />
                              </FormControl>
                            </Flex>
                            <Flex justifyContent="space-between" gap={2}>
                              <FormControl>
                                <FormLabel>Text Transform</FormLabel>
                                <Select
                                  name="textTransform"
                                  value={
                                    designElements[element.value]
                                      ?.textTransform || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "textTransform",
                                      e.target.value
                                    )
                                  }
                                >
                                  {textTransform?.map((option) => (
                                    <option value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl>
                                <FormLabel>Text Decoration</FormLabel>
                                <Select
                                  name="textDecoration"
                                  value={
                                    designElements[element.value]
                                      ?.textDecoration
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      element.value,
                                      "textDecoration",
                                      e.target.value
                                    )
                                  }
                                >
                                  {textDecoration?.map((option) => (
                                    <option value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                              </FormControl>
                            </Flex>
                            <Center>
                              <Button
                                onClick={() =>
                                  handleDeleteElement(element?.value)
                                }
                                colorScheme="red"
                                fontSize="sm"
                              >
                                Reset
                              </Button>
                            </Center>
                          </Grid>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Box>
        </DndProvider>
      )}
      {modalOpen.open && (
        <ElementContainer
          elements={elements}
          attributesData={elements.filter(
            (item: any) => item.id === modalOpen.data
          )}
          id={modalOpen.data}
          type={modalOpen.type}
          open={modalOpen.open}
          setElements={setElements}
          onClose={() =>
            setModalOpen({ open: false, data: null, type: undefined })
          }
        />
      )}
      {/* <FormModal elements={elements} designElements={designElements} /> */}
      {activePage.page === 2 && (
        <ViewFormModal
          elements={activePage.data?.elements}
          designElements={activePage.data?.designElements}
          activePage={activePage}
          setActivePage={setActivePage}
        />
      )}
    </React.Fragment>
  );
};

export default CustomOnboarding;
